import {
  PokemonType,
  SkillData,
  SkillEvent,
  SkillEvent_AnchoredInvoke,
  SkillEvent_GapClose,
  SkillEvent_Global,
  SkillEvent_Heal,
  SkillEvent_MeleeHit,
  SkillEvent_ParticleEmission,
  SkillEvent_ProjectileCast,
  SkillEvent_Shield,
  SkillEvent_Vfx,
  SkillRangeType,
  SkillType,
} from "types/battle-engine/battle";
import { Pokemon } from "./pokemon";
import { getCooldownMultiplier } from "game/duel-engine/utils/stat-scaling";

export class PokemonSkill {
  public id: string;
  public name: string;
  public data: SkillData;
  public power: number;
  public pokemon_type: PokemonType;
  public types: SkillType[];
  public range: {
    type: SkillRangeType;
    value: number;
  };
  public on_cast: SkillEvent[];
  public on_hit: SkillEvent[];

  protected cooldown: {
    remaining: number;
    total: number;
  };

  protected pokemon: Pokemon;
  protected currentTick: number;

  constructor(data: SkillData, pokemon: Pokemon) {
    this.id = data.id;
    this.name = data.name;
    this.power = data.power;
    this.data = data;
    this.pokemon_type = data.pokemon_type;
    this.types = data.types;
    this.range = data.range;
    this.pokemon = pokemon;
    this.currentTick = 0;
    this.on_cast = data.on_cast;
    this.on_hit = data.on_hit;

    // Initialize cooldown with current stats
    this.cooldown = {
      remaining: 0,
      total: this.calculateTotalCooldown(),
    };
  }

  protected calculateTotalCooldown(): number {
    const cdrMultiplier = getCooldownMultiplier(this.pokemon.effectiveSpeed);
    return Math.max(1, Math.round(this.data.cooldown_ticks * cdrMultiplier));
  }

  public get IsReady(): boolean {
    return this.cooldown.remaining <= 0;
  }

  public refreshCooldown(tick: number = 1) {
    if (this.cooldown.remaining <= 0) return;
    this.cooldown.remaining -= tick;
    if (this.cooldown.remaining < 0) this.cooldown.remaining = 0;
  }

  /* deprecated */
  public useSkill(skillData: SkillData, currentTick?: number) {
    // Recalculate total cooldown based on current stats (dynamic CDR)
    this.cooldown.total = this.calculateTotalCooldown();
    this.cooldown.remaining = this.cooldown.total;
  }

  public putOnCooldown() {
    // Recalculate total cooldown based on current stats (dynamic CDR)
    this.cooldown.total = this.calculateTotalCooldown();
    this.cooldown.remaining = this.cooldown.total;
  }

  protected triggerOnHit(
    preparedOnHitEvents: SkillEvent[],
    currentTick?: number,
  ) {
    return;
  }

  // ------------------- Event Handlers -------------------
  // These methods are used to apply the events from on_cast and on_hit
  // --------------------------------------------------------

  /**
   * ProjectileCast event handler
   */
  protected handleProjectileCast({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_ProjectileCast;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleMeleeHit({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_MeleeHit;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleGapClose({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_GapClose;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleHeal({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_Heal;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleShield({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_Shield;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleAnchoredInvoke({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_AnchoredInvoke;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleVfx({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_Vfx;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleParticleEmission({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_ParticleEmission;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }

  protected handleGlobal({
    event,
    skillData,
    currentTick,
  }: {
    event: SkillEvent_Global;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    throw new Error("Not implemented");
  }
}
